import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X, PiggyBank, Target } from 'lucide-react';
import { Presupuesto } from '../types';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';
import { cambioDeLimite, limiteEnPeriodo } from '../utils/presupuestoUtils';

interface Props {
  onClose: () => void;
  presupuestoEditar?: Presupuesto;
  /** Mes que se está viendo en Presupuestos ('YYYY-MM'): al editar, el importe es el de ese mes */
  periodoVista?: string;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const nombreMes = (periodo: string) => {
  const [a, m] = periodo.split('-').map(Number);
  return `${MESES[m - 1].toLowerCase()} de ${a}`;
};

export const ModalAgregarPresupuesto: React.FC<Props> = ({ onClose, presupuestoEditar, periodoVista }) => {
  useCerrarConEscape(onClose);
  const { crearPresupuesto, actualizarPresupuesto, categorias } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const modoEdicion = !!presupuestoEditar;
  const hoy = new Date();

  // ── Estado del formulario ─────────────────────────────────────────────────
  const [nombre, setNombre]             = useState(presupuestoEditar?.nombre || '');
  // Un mensual repetitivo puede tener un importe distinto según el mes. Al editarlo
  // desde un mes concreto, el campo muestra el importe de ESE mes y, si se cambia,
  // se pregunta si es solo para ese mes o desde ese mes en adelante.
  const ajustable = !!presupuestoEditar && presupuestoEditar.periodicidad === 'mensual'
    && presupuestoEditar.repetitivo && !!periodoVista && periodoVista >= presupuestoEditar.mes;
  const importeOriginal = presupuestoEditar
    ? (ajustable ? limiteEnPeriodo(presupuestoEditar, periodoVista!) : presupuestoEditar.limite)
    : 0;
  const [limite, setLimite]             = useState(presupuestoEditar ? importeOriginal.toString() : '');
  const [alcance, setAlcance]           = useState<'mes' | 'desde' | null>(null);
  const [categoriaId, setCategoriaId]   = useState(presupuestoEditar?.categoriaId || '');
  const [periodicidad, setPeriodicidad] = useState<'mensual' | 'anual'>(presupuestoEditar?.periodicidad || 'mensual');
  const [repetitivo, setRepetitivo]     = useState(presupuestoEditar?.repetitivo || false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  // Período mensual
  const [mes, setMes] = useState(
    presupuestoEditar?.periodicidad === 'mensual'
      ? presupuestoEditar.mes
      : hoy.toISOString().slice(0, 7)
  );

  // Período anual
  const [anio, setAnio] = useState(
    presupuestoEditar?.periodicidad === 'anual'
      ? presupuestoEditar.mes
      : hoy.getFullYear().toString()
  );

  // ── Campos nuevos (solo anuales) ──────────────────────────────────────────
  const [esObjetivoAhorro, setEsObjetivoAhorro] = useState(
    presupuestoEditar?.esObjetivoAhorro ?? false
  );
  const [tieneMesInicio, setTieneMesInicio] = useState(
    !!(presupuestoEditar?.mesInicio && presupuestoEditar?.anioInicio)
  );
  const [mesInicio, setMesInicio] = useState<number>(
    presupuestoEditar?.mesInicio ?? hoy.getMonth() + 1
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const inputClass  = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass  = "block text-gray-700 dark:text-gray-300 font-semibold mb-2";

  const categoriasGastos = categorias.filter(c => c.tipo === 'gasto');

  // Importe mensual calculado (solo informativo)
  const importeMensual = limite && !isNaN(aNumero(limite))
    ? (aNumero(limite) / 12).toFixed(2)
    : null;

  const cambiaImporte = ajustable && periodicidad === 'mensual' && repetitivo
    && !isNaN(aNumero(limite)) && aNumero(limite) !== importeOriginal;

  // ── Envío ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre || !limite || !categoriaId) { setError('Completa todos los campos'); return; }

    const anioNum = parseInt(anio, 10);

    // Validación de mesInicio
    if (periodicidad === 'anual' && tieneMesInicio) {
      if (mesInicio < 1 || mesInicio > 12) { setError('El mes de inicio debe estar entre 1 y 12'); return; }
      if (mesInicio === 1) { setError('Si el inicio es enero no es necesario indicar mes de inicio'); return; }
    }

    if (cambiaImporte && !alcance) { setError('Elige si el importe nuevo es solo para este mes o desde este mes en adelante'); return; }

    // Importe y, si toca, sus cambios por mes. Sin cambio de importe en un
    // ajustable, el importe general se queda como estaba (el campo mostraba el del mes).
    const importes = cambiaImporte && presupuestoEditar && alcance
      ? cambioDeLimite(presupuestoEditar, mes, periodoVista!, aNumero(limite), alcance)
      : { limite: ajustable && presupuestoEditar && aNumero(limite) === importeOriginal ? presupuestoEditar.limite : aNumero(limite) };

    setLoading(true);
    try {
      const datos: Omit<Presupuesto, 'id' | 'usuarioId' | 'createdAt'> = {
        nombre,
        ...importes,
        categoriaId,
        mes: periodicidad === 'mensual' ? mes : anio,
        periodicidad,
        repetitivo,
        gastado: presupuestoEditar?.gastado ?? 0,
        esObjetivoAhorro: periodicidad === 'anual' ? esObjetivoAhorro : undefined,
        mesInicio:  periodicidad === 'anual' && tieneMesInicio ? mesInicio : undefined,
        anioInicio: periodicidad === 'anual' && tieneMesInicio ? anioNum  : undefined,
        migradoDesdeMensual: presupuestoEditar?.migradoDesdeMensual,
      };

      if (modoEdicion && presupuestoEditar) {
        await actualizarPresupuesto(presupuestoEditar.id, datos);
      } else {
        await crearPresupuesto(datos);
      }
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al guardar');
    } finally { setLoading(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">

        {/* Cabecera */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {modoEdicion ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Nombre */}
          <div>
            <label className={labelClass}>Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputClass} placeholder="Ej: Vacaciones 2026" required />
          </div>

          {/* Periodicidad */}
          <div>
            <label className={labelClass}>Periodicidad</label>
            <div className="flex gap-6">
              {(['mensual', 'anual'] as const).map(p => (
                <label key={p} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value={p} checked={periodicidad === p}
                    onChange={() => setPeriodicidad(p)} className="accent-blue-600" />
                  <span className="text-gray-700 dark:text-gray-300 font-semibold capitalize">{p}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Límite */}
          <div>
            <label className={labelClass}>
              {periodicidad === 'anual' ? 'Importe anual (€)' : 'Límite mensual (€)'}
            </label>
            <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={limite} onChange={e => setLimite(e.target.value)}
              className={inputClass} placeholder="0,00" required />
            {ajustable && periodicidad === 'mensual' && repetitivo && !cambiaImporte && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                Es el importe de {nombreMes(periodoVista!)}. Si lo cambias, podrás elegir si es solo para ese mes
                o desde ese mes en adelante.
              </p>
            )}
            {cambiaImporte && (
              <fieldset className="mt-3 space-y-2">
                <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  ¿Para qué meses es el importe nuevo?
                </legend>
                {([
                  ['mes', `Solo ${nombreMes(periodoVista!)}`, `Ahora tiene €${importeOriginal.toFixed(2)}. Los demás meses no cambian.`],
                  ['desde', `Desde ${nombreMes(periodoVista!)} en adelante`, 'Los meses anteriores conservan el importe que tenían.'],
                ] as const).map(([valor, titulo, detalle]) => (
                  <label key={valor}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                      alcance === valor
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}>
                    <input type="radio" name="alcance" value={valor} checked={alcance === valor}
                      onChange={() => setAlcance(valor)} className="mt-1 accent-blue-600" />
                    <span>
                      <span className="block font-semibold text-sm text-gray-900 dark:text-white">{titulo}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{detalle}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            {periodicidad === 'anual' && importeMensual && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                Equivale a <span className="font-semibold text-blue-600 dark:text-blue-400">€{importeMensual}/mes</span>
              </p>
            )}
          </div>

          {/* Categoría */}
          <div>
            <label className={labelClass}>Categoría</label>
            <select value={categoriaId} onChange={e => setCategoriaId(e.target.value)}
              className={inputClass} required>
              <option value="">Selecciona una categoría</option>
              {categoriasGastos.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icono} {cat.nombre}</option>
              ))}
            </select>
          </div>

          {/* Período */}
          {periodicidad === 'mensual' ? (
            <div>
              <label className={labelClass}>Mes</label>
              <input type="month" value={mes} onChange={e => setMes(e.target.value)}
                className={inputClass} required />
            </div>
          ) : (
            <div>
              <label className={labelClass}>Año</label>
              <input type="number" inputMode="numeric" value={anio} onChange={e => setAnio(e.target.value)}
                min="2020" max="2100" className={inputClass} required />
            </div>
          )}

          {/* ── Campos exclusivos de presupuestos anuales ── */}
          {periodicidad === 'anual' && (
            <>
              {/* Tipo: ahorro o gasto */}
              <div>
                <label className={labelClass}>Tipo de presupuesto</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setEsObjetivoAhorro(false)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-semibold transition text-sm ${
                      !esObjetivoAhorro
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                    }`}>
                    <Target className="w-4 h-4" />
                    Control de gasto
                  </button>
                  <button
                    type="button"
                    onClick={() => setEsObjetivoAhorro(true)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-semibold transition text-sm ${
                      esObjetivoAhorro
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                    }`}>
                    <PiggyBank className="w-4 h-4" />
                    Objetivo de ahorro
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  {esObjetivoAhorro
                    ? 'Acumula traspasos a inversiones y gastos hacia este objetivo.'
                    : 'Controla que el gasto no supere el límite definido.'}
                </p>
              </div>

              {/* Mes de inicio */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={tieneMesInicio}
                    onChange={e => setTieneMesInicio(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded" />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-semibold text-sm">
                      El presupuesto comienza a mitad de año
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Los meses previos se contabilizan como consumo teórico para el análisis anual
                    </p>
                  </div>
                </label>

                {tieneMesInicio && (
                  <div>
                    <label className="block text-gray-600 dark:text-gray-400 text-sm font-semibold mb-1.5">
                      Mes de inicio
                    </label>
                    <select
                      value={mesInicio}
                      onChange={e => setMesInicio(parseInt(e.target.value, 10))}
                      className={inputClass}>
                      {MESES.map((nombre, idx) => (
                        <option key={idx + 1} value={idx + 1} disabled={idx === 0}>
                          {nombre}{idx === 0 ? ' (enero — no requiere ajuste)' : ''}
                        </option>
                      ))}
                    </select>
                    {importeMensual && mesInicio > 1 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        Se asumirá un consumo teórico de{' '}
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          €{((mesInicio - 1) * parseFloat(importeMensual)).toFixed(2)}
                        </span>{' '}
                        para los {mesInicio - 1} meses anteriores ({MESES.slice(0, mesInicio - 1).join(', ')})
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Repetitivo */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={repetitivo} onChange={e => setRepetitivo(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded" />
            <div>
              <span className="text-gray-700 dark:text-gray-300 font-semibold">Presupuesto repetitivo</span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Se crea automáticamente cada período</p>
            </div>
          </label>

          {/* Acciones */}
          <div className="flex gap-4 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Guardando...' : modoEdicion ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
